import { z } from "zod";
import { bilingualStringSchema } from "./bilingual.ts";
import { printSettingsSchema } from "./settings.ts";

// ===== Visits =====

export const visitTypeSchema = z.enum([
	"consultation",
	"follow_up",
	"procedure",
	"emergency",
	"second_opinion",
	"other",
]);
export type VisitType = z.infer<typeof visitTypeSchema>;

/** A structured ICD-10 diagnosis attached to a visit (title snapshotted). */
export const diagnosisCodeSchema = z.object({
	code: z.string().min(1).max(10),
	ar: z.string().max(200),
	en: z.string().max(200).optional(),
});
export type DiagnosisCode = z.infer<typeof diagnosisCodeSchema>;

export const patientVisitSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	appointment_id: z.string().uuid().nullable(),
	doctor_id: z.string().uuid().nullable(),
	visit_type: visitTypeSchema,
	chief_complaint: z.string().nullable(),
	// Structured SOAP note (all optional; legacy visits use notes_ar only).
	subjective: z.string().nullable(),
	objective: z.string().nullable(),
	assessment: z.string().nullable(),
	plan: z.string().nullable(),
	notes_ar: z.string().nullable(),
	notes_en: z.string().nullable(),
	diagnosis: z.array(z.string()),
	diagnosis_codes: z.array(diagnosisCodeSchema).default([]),
	treatment_summary: z.string().nullable(),
	next_visit_recommendation: z.string().nullable(),
	// Sign & lock: set once the doctor signs — the visit row becomes read-only
	// and later corrections land in addendums (see visitAddendumSchema).
	signed_at: z.string().datetime().nullable(),
	signed_by_member_id: z.string().uuid().nullable(),
	/** Display name of the signer (member title → user full name). */
	signed_by_name: z.string().nullable(),
	/** Optimistic-lock counter — send it back as expected_version on update. */
	version: z.number().int().default(1),
	occurred_at: z.string().datetime(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PatientVisitApi = z.infer<typeof patientVisitSchema>;

export const createVisitInputSchema = z.object({
	patient_id: z.string().uuid(),
	appointment_id: z.string().uuid().optional(),
	doctor_id: z.string().uuid().optional(),
	visit_type: visitTypeSchema.default("consultation"),
	chief_complaint: z.string().max(2000).optional(),
	subjective: z.string().max(5000).optional(),
	objective: z.string().max(5000).optional(),
	assessment: z.string().max(5000).optional(),
	plan: z.string().max(5000).optional(),
	notes_ar: z.string().max(5000).optional(),
	notes_en: z.string().max(5000).optional(),
	diagnosis: z.array(z.string().max(200)).max(20).default([]),
	diagnosis_codes: z.array(diagnosisCodeSchema).max(20).default([]),
	treatment_summary: z.string().max(2000).optional(),
	next_visit_recommendation: z.string().max(500).optional(),
	occurred_at: z.string().datetime().optional(),
});
export type CreateVisitInput = z.infer<typeof createVisitInputSchema>;

export const updateVisitInputSchema = createVisitInputSchema
	.partial()
	.omit({
		patient_id: true,
		appointment_id: true,
	})
	.extend({
		/** Optimistic lock: the version the client loaded. If it no longer matches
		 *  the row (another device saved first), the update is rejected with 409. */
		expected_version: z.number().int().optional(),
	});
export type UpdateVisitInput = z.infer<typeof updateVisitInputSchema>;

// ===== Visit addendums (post-sign amendment trail — «الملاحق») =====

/**
 * A timestamped, attributed correction appended to a (typically signed)
 * visit. The original note never mutates; each correction is a new row.
 */
export const visitAddendumSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	visit_id: z.string().uuid(),
	author_member_id: z.string().uuid(),
	/** Display name of the author (member title → user full name). */
	author_name: z.string().nullable(),
	body: z.string(),
	created_at: z.string().datetime(),
});
export type VisitAddendumApi = z.infer<typeof visitAddendumSchema>;

export const createVisitAddendumInputSchema = z.object({
	body: z.string().min(1).max(5000),
});
export type CreateVisitAddendumInput = z.infer<
	typeof createVisitAddendumInputSchema
>;

// ===== AI clinical scribe (dictation / free narrative → structured SOAP) =====

export const scribeSoapInputSchema = z.object({
	/** Raw dictation transcript or free-text narrative from the doctor. */
	transcript: z.string().min(1).max(8000),
	chief_complaint: z.string().max(500).optional(),
	/** Facility/specialty hint so the model tailors the note (e.g. "dermatology"). */
	specialty: z.string().max(60).optional(),
	patient_age_years: z.number().int().nonnegative().max(150).optional(),
	patient_gender: z.enum(["male", "female"]).optional(),
});
export type ScribeSoapInput = z.infer<typeof scribeSoapInputSchema>;

export const scribeSoapResultSchema = z.object({
	chief_complaint: z.string(),
	subjective: z.string(),
	objective: z.string(),
	assessment: z.string(),
	plan: z.string(),
	/** Free-text diagnosis suggestions the doctor can accept as tags. */
	suggested_diagnoses: z.array(z.string()).default([]),
	/**
	 * ICD-10 codes the scribe proposed, GROUNDED server-side against the full
	 * WHO ICD-10 dataset (fakes dropped) so a single dictation also fills the
	 * coded-diagnosis picker. The doctor reviews/edits all of it before save.
	 */
	diagnosis_codes: z.array(diagnosisCodeSchema).default([]),
	/** One-line summary of what was done / prescribed (visit field). */
	treatment_summary: z.string().default(""),
	/** Follow-up recommendation (visit field), e.g. "مراجعة بعد أسبوع". */
	next_visit_recommendation: z.string().default(""),
});
export type ScribeSoapResult = z.infer<typeof scribeSoapResultSchema>;

// ===== Visit templates (reusable pre-filled encounters — one-tap apply) =====

/**
 * The pre-fillable visit fields a template carries (mirrors the visit form).
 *
 * Deliberately EXCLUDES `objective` (O): it holds the source patient's
 * measured exam findings (vitals like BP 220/120) and templating them would
 * inject another patient's measurements into a new note — misleading clinical
 * documentation. Same principle as carry-forward, which never copies O.
 * The server additionally strips `objective` from legacy stored templates.
 */
export const visitTemplateBodySchema = z.object({
	chief_complaint: z.string().max(500).optional(),
	subjective: z.string().max(4000).optional(),
	assessment: z.string().max(4000).optional(),
	plan: z.string().max(4000).optional(),
	diagnosis: z.array(z.string().max(200)).max(20).optional(),
	diagnosis_codes: z.array(diagnosisCodeSchema).max(20).optional(),
	treatment_summary: z.string().max(1000).optional(),
	next_visit_recommendation: z.string().max(1000).optional(),
});
export type VisitTemplateBody = z.infer<typeof visitTemplateBodySchema>;

export const visitTemplateSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	visit_type: visitTypeSchema.nullable(),
	body: visitTemplateBodySchema,
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type VisitTemplateApi = z.infer<typeof visitTemplateSchema>;

export const createVisitTemplateInputSchema = z.object({
	name: z.string().min(1).max(120),
	visit_type: visitTypeSchema.optional(),
	body: visitTemplateBodySchema,
});
export type CreateVisitTemplateInput = z.infer<
	typeof createVisitTemplateInputSchema
>;

// ===== AI clinical co-pilot (point-of-care decision SUPPORT — assistive only) =====
//
// Reads the structured visit data the doctor has entered so far and returns a
// ranked differential, suggested workup, and safety red-flags. Strictly
// assistive: the doctor reviews, decides, and remains the sole responsible
// party. Every call is logged to ai_assist_log for medico-legal audit.

/** Optional vitals snapshot the doctor can feed for richer reasoning. */
export const copilotVitalsSchema = z.object({
	blood_pressure_systolic: z.number().int().min(40).max(300).optional(),
	blood_pressure_diastolic: z.number().int().min(20).max(200).optional(),
	pulse_bpm: z.number().int().min(20).max(300).optional(),
	temperature_c: z.number().min(30).max(45).optional(),
	respiratory_rate: z.number().int().min(4).max(80).optional(),
	oxygen_saturation: z.number().int().min(40).max(100).optional(),
	weight_kg: z.number().min(0.3).max(500).optional(),
	height_cm: z.number().min(20).max(260).optional(),
});
export type CopilotVitals = z.infer<typeof copilotVitalsSchema>;

export const copilotAssessInputSchema = z
	.object({
		/** Visit this assessment is tied to (for audit linkage). Optional for ad-hoc use. */
		visit_id: z.string().uuid().optional(),
		patient_id: z.string().uuid().optional(),
		chief_complaint: z.string().max(500).optional(),
		/** Subjective / HPI — symptoms, duration, history. */
		subjective: z.string().max(4000).optional(),
		/** Objective — exam findings. */
		objective: z.string().max(4000).optional(),
		/** The doctor's current working assessment, if any (co-pilot refines around it). */
		current_assessment: z.string().max(2000).optional(),
		vitals: copilotVitalsSchema.optional(),
		specialty: z.string().max(60).optional(),
		patient_age_years: z.number().int().nonnegative().max(150).optional(),
		patient_gender: z.enum(["male", "female"]).optional(),
	})
	.refine(
		(v) =>
			Boolean(
				v.chief_complaint ||
					v.subjective ||
					v.objective ||
					v.current_assessment,
			),
		{
			message:
				"At least one of chief_complaint, subjective, objective, or current_assessment is required.",
		},
	);
export type CopilotAssessInput = z.infer<typeof copilotAssessInputSchema>;

export const copilotLikelihood = z.enum(["high", "moderate", "low"]);

export const copilotDifferentialSchema = z.object({
	/** Diagnosis name (same language as the input, default Arabic). */
	name: z.string(),
	/** Best-guess ICD-10 code if the model is confident; else empty. */
	icd10_hint: z.string().default(""),
	likelihood: copilotLikelihood,
	/** One short clinical line: why this is / isn't likely. */
	rationale: z.string(),
});
export type CopilotDifferential = z.infer<typeof copilotDifferentialSchema>;

export const copilotInvestigationSchema = z.object({
	category: z.enum(["lab", "imaging", "procedure", "other"]),
	name: z.string(),
	reason: z.string(),
});
export type CopilotInvestigation = z.infer<typeof copilotInvestigationSchema>;

export const copilotRedFlagSchema = z.object({
	finding: z.string(),
	/** Recommended action — what the doctor should consider doing now. */
	action: z.string(),
	severity: z.enum(["urgent", "caution"]).default("caution"),
});
export type CopilotRedFlag = z.infer<typeof copilotRedFlagSchema>;

export const copilotAssessResultSchema = z.object({
	/** Short clinical reasoning summary. */
	summary: z.string().default(""),
	differentials: z.array(copilotDifferentialSchema).default([]),
	recommended_investigations: z.array(copilotInvestigationSchema).default([]),
	red_flags: z.array(copilotRedFlagSchema).default([]),
});
export type CopilotAssessResult = z.infer<typeof copilotAssessResultSchema>;

// ===== AI after-visit summary (patient-facing, plain language) =====
//
// After the doctor saves a visit, generate a clear summary the patient can take
// home: what was found, meds + how to take them, care instructions, warning
// signs, and follow-up. Assistive — the doctor reviews/edits before handing it
// over. Logged to ai_assist_log (kind='visit_summary').

export const avsInputSchema = z
	.object({
		visit_id: z.string().uuid().optional(),
		patient_id: z.string().uuid().optional(),
		chief_complaint: z.string().max(500).optional(),
		assessment: z.string().max(2000).optional(),
		diagnosis: z.array(z.string()).max(40).optional(),
		plan: z.string().max(4000).optional(),
		treatment_summary: z.string().max(2000).optional(),
		next_visit_recommendation: z.string().max(500).optional(),
		patient_age_years: z.number().int().nonnegative().max(150).optional(),
		patient_gender: z.enum(["male", "female"]).optional(),
		/** Output language — patient-facing, defaults to Arabic. */
		language: z.enum(["ar", "en", "ckb"]).default("ar"),
	})
	.refine(
		(v) =>
			Boolean(
				v.assessment || v.plan || v.treatment_summary || v.diagnosis?.length,
			),
		{
			message:
				"At least one of assessment, plan, treatment_summary, or diagnosis is required.",
		},
	);
export type AvsInput = z.infer<typeof avsInputSchema>;

export const avsMedicationSchema = z.object({
	name: z.string(),
	/** Plain-language instructions, e.g. "حبة بعد الأكل ٣ مرات يومياً لمدة ٥ أيام". */
	instructions: z.string(),
});
export type AvsMedication = z.infer<typeof avsMedicationSchema>;

export const avsResultSchema = z.object({
	/** Plain-language recap of the visit for the patient. */
	summary: z.string().default(""),
	medications: z.array(avsMedicationSchema).default([]),
	/** Care / self-management instructions (do's). */
	care_instructions: z.array(z.string()).default([]),
	/** When to seek urgent care / come back sooner. */
	warning_signs: z.array(z.string()).default([]),
	follow_up: z.string().default(""),
});
export type AvsResult = z.infer<typeof avsResultSchema>;

// ===== AI computer-assisted coding (ICD-10 suggestion, grounded) =====
//
// From the visit's assessment/diagnosis text, the model proposes codes that it
// must pick FROM Klyra's curated ICD-10 library; the server then grounds the
// list against the library (matchIcd10Codes) so a hallucinated code can never
// reach the chart. Returns library DiagnosisCode snapshots the doctor accepts.

export const icdSuggestInputSchema = z
	.object({
		visit_id: z.string().uuid().optional(),
		patient_id: z.string().uuid().optional(),
		chief_complaint: z.string().max(500).optional(),
		assessment: z.string().max(2000).optional(),
		diagnosis: z.array(z.string()).max(40).optional(),
		plan: z.string().max(4000).optional(),
	})
	.refine(
		(v) =>
			Boolean(
				v.assessment || v.chief_complaint || v.plan || v.diagnosis?.length,
			),
		{
			message:
				"At least one of assessment, chief_complaint, plan, or diagnosis is required.",
		},
	);
export type IcdSuggestInput = z.infer<typeof icdSuggestInputSchema>;

export const icdSuggestResultSchema = z.object({
	codes: z.array(diagnosisCodeSchema).default([]),
});
export type IcdSuggestResult = z.infer<typeof icdSuggestResultSchema>;

// ===== Prescriptions =====

export const prescriptionItemSchema = z.object({
	medication_name: z.string().min(1).max(200),
	dosage: z.string().min(1).max(100),
	frequency: z.string().min(1).max(100),
	duration_days: z.number().int().positive().max(365).optional(),
	instructions: z.string().max(500).optional(),
});
export type PrescriptionItem = z.infer<typeof prescriptionItemSchema>;

/**
 * Draft-tolerant medication row for prescription CREATE/UPDATE input: the
 * mandatory text fields may arrive empty at the parse layer so the server can
 * reject with a 400 that NAMES the incomplete rows (prescription-service
 * enforces completeness). Silent filtering of half-written rows is forbidden —
 * a dropped drug invalidates the interaction check that ran on the full list.
 */
export const prescriptionItemDraftSchema = z.object({
	medication_name: z.string().max(200).default(""),
	dosage: z.string().max(100).default(""),
	frequency: z.string().max(100).default(""),
	duration_days: z.number().int().positive().max(365).optional(),
	instructions: z.string().max(500).optional(),
});
export type PrescriptionItemDraft = z.infer<typeof prescriptionItemDraftSchema>;

// ===== Prescription templates (reusable medication sets) =====

export const prescriptionTemplateSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	items: z.array(prescriptionItemSchema),
	notes_ar: z.string().nullable(),
	created_at: z.string().datetime(),
});
export type PrescriptionTemplateApi = z.infer<
	typeof prescriptionTemplateSchema
>;

export const createPrescriptionTemplateInputSchema = z.object({
	name: z.string().min(2).max(120),
	items: z.array(prescriptionItemSchema).min(1).max(30),
	notes_ar: z.string().max(2000).nullable().optional(),
});
export type CreatePrescriptionTemplateInput = z.infer<
	typeof createPrescriptionTemplateInputSchema
>;

export const prescriptionSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	doctor_id: z.string().uuid().nullable(),
	items: z.array(prescriptionItemSchema),
	notes_ar: z.string().nullable(),
	notes_en: z.string().nullable(),
	routed_to_pharmacy_id: z.string().uuid().nullable(),
	dispensed_at: z.string().datetime().nullable(),
	pickup_code: z.string().nullable(),
	share_token: z.string(),
	issued_at: z.string().datetime(),
	sent_to_patient_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PrescriptionApi = z.infer<typeof prescriptionSchema>;

/**
 * Public payload returned from /rx/:token. Sanitized — never includes
 * phone, allergies, national_id, or any identifying patient detail
 * beyond the first name.
 */
export const publicPrescriptionSchema = z.object({
	prescription: z.object({
		items: z.array(prescriptionItemSchema),
		notes_ar: z.string().nullable(),
		notes_en: z.string().nullable(),
		issued_at: z.string().datetime(),
	}),
	patient: z.object({
		display_name: z.string(),
		/** The patient's language — the public page opens in it by default. */
		preferred_language: z.enum(["ar", "en", "ckb"]),
	}),
	doctor: z.object({
		display_name: z.string().nullable(),
		/** Prescriber's medical-syndicate / registration number, if recorded. */
		license_number: z.string().nullable(),
	}),
	clinic: z.object({
		name: bilingualStringSchema,
		slug: z.string(),
		logo_url: z.string().nullable(),
		primary_color: z.string().nullable(),
	}),
	/**
	 * Present ONLY when the prescription was routed to a linked pharmacy: the
	 * pickup code the patient must give the pharmacy to collect. The code is
	 * deliberately hidden from the pharmacy's side (they ask the patient), so
	 * the patient needs to SEE it here — previously it was relayed verbally and
	 * could be lost. Null for a normal (non-routed) prescription.
	 */
	pharmacy: z
		.object({
			name: bilingualStringSchema,
			pickup_code: z.string(),
		})
		.nullable(),
});
export type PublicPrescriptionApi = z.infer<typeof publicPrescriptionSchema>;

export const createPrescriptionInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	doctor_id: z.string().uuid().optional(),
	// Draft-tolerant at the parse layer so the service can 400 with the names
	// of incomplete rows (never silently dropped). See prescriptionItemDraftSchema.
	items: z.array(prescriptionItemDraftSchema).min(1).max(20),
	notes_ar: z.string().max(2000).optional(),
	notes_en: z.string().max(2000).optional(),
	/** Route this prescription to a linked pharmacy (must be an active link). */
	routed_to_pharmacy_id: z.string().uuid().optional(),
	/**
	 * NKDA gate override: when the patient's allergy_status is 'unrecorded'
	 * the server refuses to issue unless the prescriber explicitly accepts
	 * responsibility («متابعة على مسؤوليتي»). Audited.
	 */
	allergy_unrecorded_override: z.boolean().optional(),
	/**
	 * Hard safety-gate overrides. The server runs a DETERMINISTIC safety check
	 * on every issue (drug–drug, drug–allergy, drug–condition, teratogen-in-
	 * active-pregnancy, and max-daily-dose). A CRITICAL finding blocks the
	 * prescription (422) unless the prescriber explicitly overrides — and every
	 * override is recorded in the audit log with the exact warnings accepted.
	 * Two separate acks so accepting an allergy risk never silently waives an
	 * unrelated interaction risk.
	 */
	allergy_override: z.boolean().optional(),
	interaction_override: z.boolean().optional(),
	/** Separate ack for a teratogen in an active pregnancy — an interaction
	 *  override must not silently waive it. */
	pregnancy_override: z.boolean().optional(),
	/**
	 * MANDATORY free-text clinical justification whenever ANY critical safety
	 * override above is used. The server refuses to issue an overridden Rx
	 * without it (min length enforced server-side) and records it verbatim in
	 * the tamper-evident audit log. A bare boolean is no longer enough to waive
	 * a teratogen-in-pregnancy or severe-allergy contraindication.
	 */
	override_justification: z.string().max(1000).optional(),
});
export type CreatePrescriptionInput = z.infer<
	typeof createPrescriptionInputSchema
>;

export const updatePrescriptionInputSchema = createPrescriptionInputSchema
	.partial()
	.omit({
		patient_id: true,
	});
export type UpdatePrescriptionInput = z.infer<
	typeof updatePrescriptionInputSchema
>;

// ===== AI prescription draft (ambient: visit plan → structured Rx draft) =====
//
// From the documented assessment/plan, the model proposes structured
// prescription items the doctor reviews, edits, and issues. Patient allergies
// are passed so the model avoids them — AND the existing allergy + drug-
// interaction checks still run on the draft before it can be issued. Assistive
// only; never auto-issued. Logged to ai_assist_log (kind='rx_suggest').

export const rxSuggestInputSchema = z
	.object({
		visit_id: z.string().uuid().optional(),
		patient_id: z.string().uuid().optional(),
		chief_complaint: z.string().max(500).optional(),
		assessment: z.string().max(2000).optional(),
		diagnosis: z.array(z.string()).max(40).optional(),
		plan: z.string().max(4000).optional(),
		patient_age_years: z.number().int().nonnegative().max(150).optional(),
		patient_gender: z.enum(["male", "female"]).optional(),
		/** Known patient allergy substances — the model must avoid these. */
		allergies: z.array(z.string()).max(50).optional(),
	})
	.refine((v) => Boolean(v.assessment || v.plan || v.diagnosis?.length), {
		message: "At least one of assessment, plan, or diagnosis is required.",
	});
export type RxSuggestInput = z.infer<typeof rxSuggestInputSchema>;

export const rxSuggestResultSchema = z.object({
	items: z.array(prescriptionItemSchema).default([]),
	/**
	 * Why the model declined (only when items is empty) — e.g. conflicting
	 * differentials or a plan that defers treatment. Shown to the doctor so an
	 * empty draft reads as guidance, not as a malfunction.
	 */
	reason: z.string().default(""),
});
export type RxSuggestResult = z.infer<typeof rxSuggestResultSchema>;

// ===== Medications library =====

export const clinicMedicationSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	default_dosage: z.string().nullable(),
	default_frequency: z.string().nullable(),
	default_duration_days: z.number().int().nullable(),
	default_instructions: z.string().nullable(),
	usage_count: z.number().int(),
	last_used_at: z.string().datetime().nullable(),
	is_pinned: z.boolean(),
	is_hidden: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ClinicMedicationApi = z.infer<typeof clinicMedicationSchema>;

export const createMedicationInputSchema = z.object({
	name: z.string().min(1).max(200),
	default_dosage: z.string().max(100).optional(),
	default_frequency: z.string().max(100).optional(),
	default_duration_days: z.number().int().positive().max(365).optional(),
	default_instructions: z.string().max(500).optional(),
	is_pinned: z.boolean().optional(),
});
export type CreateMedicationInput = z.infer<typeof createMedicationInputSchema>;

export const updateMedicationInputSchema = z.object({
	name: z.string().min(1).max(200).optional(),
	default_dosage: z.string().max(100).nullable().optional(),
	default_frequency: z.string().max(100).nullable().optional(),
	default_duration_days: z
		.number()
		.int()
		.positive()
		.max(365)
		.nullable()
		.optional(),
	default_instructions: z.string().max(500).nullable().optional(),
	is_pinned: z.boolean().optional(),
	is_hidden: z.boolean().optional(),
});
export type UpdateMedicationInput = z.infer<typeof updateMedicationInputSchema>;

export const medicationSearchQuerySchema = z.object({
	q: z.string().max(100).optional(),
	limit: z.coerce.number().int().min(1).max(20).default(8),
});
export type MedicationSearchQuery = z.infer<typeof medicationSearchQuerySchema>;

// ===== Vitals =====

/**
 * Per-row vitals reading. All clinical fields are optional — the nurse
 * may record only what's relevant (e.g. weight-only at a follow-up).
 * Ranges are conservative on the upper end so we catch typos (BP 200/300
 * is a typo, not real) but accept anything physiologically plausible.
 */
export const patientVitalsSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	blood_pressure_systolic: z.number().int().nullable(),
	blood_pressure_diastolic: z.number().int().nullable(),
	pulse_bpm: z.number().int().nullable(),
	temperature_c: z.number().nullable(),
	weight_kg: z.number().nullable(),
	height_cm: z.number().nullable(),
	oxygen_saturation: z.number().int().nullable(),
	notes: z.string().nullable(),
	recorded_at: z.string().datetime(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PatientVitalsApi = z.infer<typeof patientVitalsSchema>;

export const createVitalsInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	// Clinical ranges — generous upper bounds for typo catching, NOT
	// for medical implausibility (a patient CAN have BP 220 in crisis).
	blood_pressure_systolic: z.number().int().min(40).max(300).optional(),
	blood_pressure_diastolic: z.number().int().min(20).max(200).optional(),
	pulse_bpm: z.number().int().min(20).max(300).optional(),
	temperature_c: z.number().min(20).max(45).optional(),
	weight_kg: z.number().min(0.5).max(500).optional(),
	height_cm: z.number().min(20).max(260).optional(),
	oxygen_saturation: z.number().int().min(50).max(100).optional(),
	notes: z.string().max(2000).optional(),
	recorded_at: z.string().datetime().optional(),
});
export type CreateVitalsInput = z.infer<typeof createVitalsInputSchema>;

export const updateVitalsInputSchema = createVitalsInputSchema.partial().omit({
	patient_id: true,
});
export type UpdateVitalsInput = z.infer<typeof updateVitalsInputSchema>;

/**
 * BMI calculator — derived field, never stored. Returns null when the
 * inputs are missing/zero so the caller can show "—" instead of NaN.
 */
export function computeBmi(
	weightKg: number | null,
	heightCm: number | null,
): number | null {
	if (!weightKg || !heightCm || heightCm <= 0) return null;
	const meters = heightCm / 100;
	return Math.round((weightKg / (meters * meters)) * 10) / 10;
}

/** Standard adult BMI classification. */
export type BmiCategory =
	| "underweight"
	| "normal"
	| "overweight"
	| "obese"
	| null;
export function bmiCategory(bmi: number | null): BmiCategory {
	if (bmi === null) return null;
	if (bmi < 18.5) return "underweight";
	if (bmi < 25) return "normal";
	if (bmi < 30) return "overweight";
	return "obese";
}

// ===== Lab orders =====

/** Money amount as a numeric string (IQD has no fractional unit in practice). */
export const moneyStringSchema = z
	.string()
	.regex(/^\d{1,12}(\.\d{1,2})?$/u, "Invalid amount");

/** Lab order worklist status. 'rejected' added in Sprint 2 (ISO 15189 §5.4.6). */
export const labOrderStatusSchema = z.enum([
	"pending",
	"completed",
	"cancelled",
	"rejected",
]);
export type LabOrderStatus = z.infer<typeof labOrderStatusSchema>;

/** Order priority — 'stat' surfaces first on the lab worklist. */
export const labUrgencySchema = z.enum(["routine", "stat"]);
export type LabUrgency = z.infer<typeof labUrgencySchema>;

/**
 * Specimen-rejection reason vocabulary — kept in sync with the CHECK
 * constraint in migration 0069. Categories cover the most common
 * pre-analytical failures observed across walk-in and referral labs.
 */
export const specimenRejectionReasonSchema = z.enum([
	"hemolyzed",
	"lipemic",
	"clotted",
	"insufficient_volume",
	"wrong_tube",
	"expired",
	"unlabeled",
	"leaking",
	"other",
]);
export type SpecimenRejectionReason = z.infer<
	typeof specimenRejectionReasonSchema
>;

/** Payload for POST /clinical/lab-orders/:id/reject. */
export const rejectLabOrderInputSchema = z.object({
	reason: specimenRejectionReasonSchema,
	notes: z.string().max(2000).nullable().optional(),
});
export type RejectLabOrderInput = z.infer<typeof rejectLabOrderInputSchema>;

/** Abnormal-flag classification for a structured lab value.
 *  `needs_review` = a numeric value the system could NOT assess (no reference
 *  range AND no critical thresholds configured for the test). Surfacing it as
 *  this instead of falsely "normal" makes an un-assessable — possibly dangerous
 *  — result visible for a human to read (F1.2). It is NON-critical: it never
 *  fires the critical-value alert pipeline (which only fires on critical_*). */
export const abnormalFlagSchema = z.enum([
	"critical_low",
	"low",
	"normal",
	"high",
	"critical_high",
	"abnormal",
	"needs_review",
]);
export type AbnormalFlag = z.infer<typeof abnormalFlagSchema>;

export const labTestItemSchema = z.object({
	name: z.string().min(1).max(200),
	instructions: z.string().max(500).optional(),
	/** Per-line price (numeric string) for lab walk-ins. */
	price: moneyStringSchema.optional(),
});
export type LabTestItem = z.infer<typeof labTestItemSchema>;

export const labOrderSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	doctor_id: z.string().uuid().nullable(),
	tests: z.array(labTestItemSchema),
	status: labOrderStatusSchema,
	urgency: labUrgencySchema,
	total_price: z.string().nullable(),
	payment_method: z.string().nullable().optional(),
	completed_at: z.string().datetime().nullable(),
	referral_doctor_id: z.string().uuid().nullable(),
	referral_pct: z.string().nullable(),
	ai_interpretation: z.string().nullable(),
	notes_ar: z.string().nullable(),
	notes_en: z.string().nullable(),
	/** When set, the linked lab (clinics.id) this order was routed to. */
	routed_to_lab_id: z.string().uuid().nullable(),
	/** Short pickup code given to the patient (routed referrals only). */
	pickup_code: z.string().nullable(),
	/** ISO 15189 §5.4.4 accession number ("<prefix>-NNNNNN", null on legacy rows). */
	accession_number: z.string().nullable(),
	/** Sprint 3 #2: when set, this order was auto-created by a reflex rule. */
	triggered_by_order_id: z.string().uuid().nullable(),
	/** Specimen rejection metadata — populated only when status='rejected'. */
	rejection_reason: specimenRejectionReasonSchema.nullable(),
	rejection_notes: z.string().nullable(),
	rejected_at: z.string().datetime().nullable(),
	rejected_by_member_id: z.string().uuid().nullable(),
	/** Specimen lifecycle stamps (ISO 15189 §5.4/§5.5, migration 0153). */
	specimen_collected_at: z.string().datetime().nullable().optional(),
	specimen_collected_by_member_id: z.string().uuid().nullable().optional(),
	specimen_received_at: z.string().datetime().nullable().optional(),
	specimen_received_by_member_id: z.string().uuid().nullable().optional(),
	/** Stamping members' display names — populated by GET /lab-orders/:id only. */
	specimen_collected_by_name: z.string().nullable().optional(),
	specimen_received_by_name: z.string().nullable().optional(),
	/** When set, a "repeat this test" reminder is due on this date (doctor follow-up). */
	recheck_at: z.string().datetime().nullable(),
	/** Referral ownership: when this lab order was created by accepting a referral. */
	source_referral_order_id: z.string().uuid().nullable(),
	referring_clinic_name: z.string().nullable(),
	referring_doctor_name: z.string().nullable(),
	/** Commission % snapshot owed to the referring clinic (frozen at accept). */
	referral_commission_pct: z.string().nullable().optional(),
	share_token: z.string(),
	ordered_at: z.string().datetime(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type LabOrderApi = z.infer<typeof labOrderSchema>;

/** Set/clear a recheck (repeat-test) reminder on a lab order. null clears it. */
export const setLabOrderRecheckInputSchema = z.object({
	recheck_at: z.string().datetime().nullable(),
});
export type SetLabOrderRecheckInput = z.infer<
	typeof setLabOrderRecheckInputSchema
>;

export const createLabOrderInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	doctor_id: z.string().uuid().optional(),
	tests: z.array(labTestItemSchema).min(1).max(30),
	/** Order priority — defaults to 'routine' server-side when omitted. */
	urgency: labUrgencySchema.optional(),
	/** Priced total for lab walk-ins. Server recomputes/validates if omitted. */
	total_price: moneyStringSchema.optional(),
	/** Payment method captured for the daily revenue report (walk-ins). */
	payment_method: z.enum(["cash", "card", "transfer", "other"]).optional(),
	/** Referring doctor (lab walk-ins) — server snapshots their commission %. */
	referral_doctor_id: z.string().uuid().optional(),
	notes_ar: z.string().max(2000).optional(),
	notes_en: z.string().max(2000).optional(),
	/** Route this order to a linked lab (must be an active facility link). */
	routed_to_lab_id: z.string().uuid().optional(),
	/**
	 * Home collection (phlebotomy at the patient's location). When present, the
	 * order is created as a scheduled home collection: a collector visits, draws
	 * the sample, marks it collected — then it flows into the normal worklist.
	 */
	collection: z
		.object({
			address: z.string().min(3).max(500),
			scheduled_at: z.string().datetime(),
			assigned_member_id: z.string().uuid().nullable().optional(),
			notes: z.string().max(500).nullable().optional(),
		})
		.optional(),
});
export type CreateLabOrderInput = z.infer<typeof createLabOrderInputSchema>;

// ===== Home collection (lab phlebotomy dispatch) =====

export const labCollectionStatusSchema = z.enum([
	"scheduled",
	"collected",
	"cancelled",
]);
export type LabCollectionStatus = z.infer<typeof labCollectionStatusSchema>;

/** One row in the lab's home-collection dispatch list. */
export const homeCollectionSchema = z.object({
	id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string(),
	patient_phone: z.string().nullable(),
	tests: z.array(labTestItemSchema),
	total_price: z.string().nullable(),
	accession_number: z.string().nullable(),
	collection_status: labCollectionStatusSchema,
	collection_address: z.string().nullable(),
	collection_scheduled_at: z.string().datetime().nullable(),
	collection_assigned_member_id: z.string().uuid().nullable(),
	collection_assigned_member_name: z.string().nullable(),
	collection_collected_at: z.string().datetime().nullable(),
	collection_notes: z.string().nullable(),
	ordered_at: z.string().datetime(),
});
export type HomeCollectionApi = z.infer<typeof homeCollectionSchema>;

export const homeCollectionsQuerySchema = z.object({
	status: labCollectionStatusSchema.optional(),
});
export type HomeCollectionsQuery = z.infer<typeof homeCollectionsQuerySchema>;

/** Update a home collection: assign a collector, reschedule, or change status. */
export const updateHomeCollectionInputSchema = z
	.object({
		status: labCollectionStatusSchema.optional(),
		assigned_member_id: z.string().uuid().nullable().optional(),
		scheduled_at: z.string().datetime().optional(),
		address: z.string().min(3).max(500).optional(),
		notes: z.string().max(500).nullable().optional(),
	})
	.refine((v) => Object.keys(v).length > 0, { message: "No changes provided" });
export type UpdateHomeCollectionInput = z.infer<
	typeof updateHomeCollectionInputSchema
>;

/**
 * Edit the tests + prices on an existing, still-open lab order. Lets a lab add
 * tests a walk-in/referred patient asks for and adjust pricing before the order
 * is completed. The server recomputes total_price from the tests when omitted.
 */
export const updateLabOrderTestsInputSchema = z.object({
	tests: z.array(labTestItemSchema).min(1).max(30),
	total_price: moneyStringSchema.nullable().optional(),
});
export type UpdateLabOrderTestsInput = z.infer<
	typeof updateLabOrderTestsInputSchema
>;

/** A lab a clinic is linked to (clinic-facing picker for routing orders). */
export const linkedLabSchema = z.object({
	id: z.string().uuid(),
	name: bilingualStringSchema,
	slug: z.string(),
});
export type LinkedLabApi = z.infer<typeof linkedLabSchema>;

// ===== Lab worklist (standalone-lab facing) =====

/** Derived specimen lifecycle state (ISO 15189 §5.4/§5.5, migration 0153). */
export const specimenStateSchema = z.enum([
	"awaiting_collection",
	"collected",
	"received",
]);
export type SpecimenState = z.infer<typeof specimenStateSchema>;

export const labWorklistItemSchema = z.object({
	id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string(),
	patient_phone: z.string().nullable(),
	tests: z.array(labTestItemSchema),
	status: labOrderStatusSchema,
	urgency: labUrgencySchema,
	total_price: z.string().nullable(),
	share_token: z.string(),
	ordered_at: z.string().datetime(),
	completed_at: z.string().datetime().nullable(),
	/** Whether any structured result values have been entered for this order. */
	has_results: z.boolean(),
	/**
	 * Number of entered values still awaiting clinician sign-off
	 * (verified_at IS NULL). Unverified values never reach the patient's
	 * public report, so the worklist surfaces them as "بانتظار اعتماد".
	 */
	unverified_count: z.number().int(),
	/** Short pickup code (routed referrals only). */
	pickup_code: z.string().nullable(),
	/** Accession number (ISO 15189 §5.4.4). */
	accession_number: z.string().nullable(),
	/** Source order id when this order was auto-created by a reflex rule. */
	triggered_by_order_id: z.string().uuid().nullable(),
	/** Specimen rejection (populated only for status='rejected' rows). */
	rejection_reason: specimenRejectionReasonSchema.nullable(),
	rejected_at: z.string().datetime().nullable(),
	/** Referring doctor name when this order came from an accepted referral. */
	referring_doctor_name: z.string().nullable().optional(),
	/**
	 * Specimen lifecycle state derived from the order's stamps (migration
	 * 0153): 'awaiting_collection' (neither set) → 'collected' → 'received'.
	 */
	specimen_state: specimenStateSchema.optional(),
});
export type LabWorklistItem = z.infer<typeof labWorklistItemSchema>;

export const labWorklistQuerySchema = z.object({
	status: labOrderStatusSchema.optional(),
	limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type LabWorklistQuery = z.infer<typeof labWorklistQuerySchema>;

// ===== Lab order completion readiness (integrity gate for "تمت") =====
//
// Completing an order delivers the report to the patient / referring doctor,
// so it must be blocked while: ordered tests have no result, entered values
// await clinician sign-off, or critical values lack documented notification
// (ISO 15189 §5.8.7). GET /lab-orders/:id/completion-readiness returns this
// shape so the UI can render the obstacles BEFORE the user hits the wall;
// POST /lab-orders/:id/complete re-checks server-side (the hard gate).

export const labCompletionBlockerTypeSchema = z.enum([
	"missing_results",
	"unverified_values",
	"critical_pending",
]);
export type LabCompletionBlockerType = z.infer<
	typeof labCompletionBlockerTypeSchema
>;

export const labCompletionBlockerSchema = z.object({
	type: labCompletionBlockerTypeSchema,
	/** Test names involved (for display). */
	tests: z.array(z.string()),
	/** For unverified_values: the value ids so the UI can offer "verify all". */
	value_ids: z.array(z.string().uuid()).optional(),
});
export type LabCompletionBlocker = z.infer<typeof labCompletionBlockerSchema>;

export const labCompletionReadinessSchema = z.object({
	ready: z.boolean(),
	blockers: z.array(labCompletionBlockerSchema),
});
export type LabCompletionReadiness = z.infer<
	typeof labCompletionReadinessSchema
>;

// ===== Lab safety-net seeding (default delta + reflex rules) =====
//
// Fresh labs start with ZERO delta/reflex rules, which silently disables the
// safety nets. POST /clinical/lab-safety/seed-defaults plants conservative
// delta rules for ~20 common analytes + standard reflex rules (idempotent).

export const labSafetySeedResultSchema = z.object({
	/** Delta rules written in this run (existing custom rules are never touched). */
	delta_rules_set: z.number().int(),
	/** Analytes skipped because a rule already exists. */
	delta_rules_skipped: z.number().int(),
	/** Catalogue tests created because the analyte wasn't in the catalogue yet. */
	tests_created: z.number().int(),
	reflex_rules_created: z.number().int(),
	reflex_rules_skipped: z.number().int(),
	/** Critical-threshold reference ranges seeded (only when the test had none
	 *  AND its unit matched a known panic-value unit). Optional for back-compat. */
	critical_ranges_set: z.number().int().optional(),
});
export type LabSafetySeedResult = z.infer<typeof labSafetySeedResultSchema>;

/**
 * Public-safe lab order view (no phone/MRN/national_id), returned from
 * GET /api/v1/lab/:token. Same shape as PublicPrescriptionApi for
 * symmetry — the patient-facing page can re-use the layout.
 */
export const publicLabResultEntrySchema = z.object({
	id: z.string().uuid(),
	caption: z.string().nullable(),
	mime_type: z.string(),
	taken_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	view_url: z.string().nullable(),
});
export type PublicLabResultEntry = z.infer<typeof publicLabResultEntrySchema>;

/** A structured result value as shown on the public page (no internals). */
export const publicLabValueSchema = z.object({
	test_name: z.string(),
	value_numeric: z.number().nullable(),
	value_text: z.string().nullable(),
	unit: z.string().nullable(),
	reference_low: z.number().nullable(),
	reference_high: z.number().nullable(),
	abnormal_flag: abnormalFlagSchema,
	/**
	 * Parent panel name when this value is a component of an ordered panel
	 * (e.g. WBC → "CBC"). Lets the patient report render the panel as one
	 * table instead of 13 loose rows. Null = standalone value.
	 */
	panel: z.string().nullable(),
	/**
	 * True when this value was amended after release (ISO 15189 §5.8.3) —
	 * the report shows an «معدّل» marker next to it.
	 */
	amended: z.boolean().optional(),
});
export type PublicLabValue = z.infer<typeof publicLabValueSchema>;

export const publicLabOrderSchema = z.object({
	lab_order: z.object({
		tests: z.array(labTestItemSchema),
		notes_ar: z.string().nullable(),
		notes_en: z.string().nullable(),
		ordered_at: z.string().datetime(),
	}),
	/** Structured result values (lab walk-ins), color-coded by abnormal_flag. */
	values: z.array(publicLabValueSchema),
	/** AI-generated patient-friendly Arabic interpretation + disclaimer. */
	ai_interpretation: z.string().nullable(),
	patient: z.object({
		display_name: z.string(),
		/** The patient's language — the public page opens in it by default. */
		preferred_language: z.enum(["ar", "en", "ckb"]),
	}),
	doctor: z.object({
		display_name: z.string().nullable(),
	}),
	clinic: z.object({
		name: bilingualStringSchema,
		slug: z.string(),
		logo_url: z.string().nullable(),
		primary_color: z.string().nullable(),
	}),
	/**
	 * Performing-lab provenance: set when these results were run at a linked lab
	 * (referral). The patient report shows "performed at X" so the issuing clinic
	 * never appears to have run tests it didn't. Null = performed in-house.
	 */
	performed_by: z.string().nullable(),
	/**
	 * True when ANY released value on this report was amended post-release —
	 * the header shows «تقرير معدّل — يلغي الإصدارات السابقة» (§5.8.3).
	 */
	amended: z.boolean().optional(),
	/** Per-facility print layout (paper size, header mode, footer, signature). */
	print: printSettingsSchema.nullable(),
	/**
	 * Results uploaded by the clinic for this order. Empty until staff
	 * attach files; once attached the patient sees download links here
	 * on the same /lab/<token> permalink they got at order-time.
	 */
	results: z.array(publicLabResultEntrySchema),
});
export type PublicLabOrderApi = z.infer<typeof publicLabOrderSchema>;

// ===== Reference ranges (per-test, age/sex aware) =====
//
// Defined before clinicLabTestSchema because the test schema embeds an
// array of reference ranges. The resolver + age helper are pure functions
// reused by server (insert path) and web (preview + range editor).

/**
 * One configured reference range for a clinic_lab_tests row. Tests with
 * non-empty reference_ranges use these; tests with empty reference_ranges
 * fall back to the flat default_ref_low/default_ref_high columns.
 *
 * SINGLE SOURCE OF TRUTH (F5.2): this is the canonical ReferenceRange shape.
 * @klyra/db imports the type from here for its `reference_ranges` jsonb column,
 * so there is no second hand-maintained copy to drift. (@klyra/shared still
 * cannot import @klyra/db — the dependency only goes db → shared.)
 */
export const referenceRangeSchema = z.object({
	sex: z.enum(["male", "female"]).nullable().optional(),
	age_min: z.number().nullable().optional(),
	age_max: z.number().nullable().optional(),
	unit: z.string().max(50).nullable().optional(),
	ref_low: z.number().nullable(),
	ref_high: z.number().nullable(),
	critical_low: z.number().nullable().optional(),
	critical_high: z.number().nullable().optional(),
	label: z.string().max(120).nullable().optional(),
});
export type ReferenceRange = z.infer<typeof referenceRangeSchema>;

/** Patient context for range resolution. */
export interface PatientForRange {
	gender?: string | null;
	date_of_birth?: string | null;
}

/**
 * Compute a patient's age in years from an ISO date string. Returns null
 * when DOB is missing or unparseable — caller should treat as "age unknown".
 */
export function computeAgeYears(
	dateOfBirth: string | null | undefined,
): number | null {
	if (!dateOfBirth) return null;
	const dob = new Date(dateOfBirth);
	if (Number.isNaN(dob.getTime())) return null;
	const now = new Date();
	let age = now.getFullYear() - dob.getFullYear();
	const m = now.getMonth() - dob.getMonth();
	if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
	return age;
}

/**
 * Pick the single best reference range for a patient out of a configured
 * array. The algorithm:
 *
 *   1. Discard ranges whose sex/age constraints don't match the patient.
 *      A range with `age_min/age_max` requires the patient to have a DOB;
 *      ranges with no age constraints work for any patient.
 *   2. Score remaining candidates by specificity (sex-specific = +2,
 *      age-bounded = +1 each). Most specific wins; ties go to first.
 *   3. Returns null when no range matches (caller should fall back to
 *      the flat defaults on clinic_lab_tests).
 *
 * Age semantics: age_min is inclusive, age_max is exclusive. So a range
 * `{ age_min: 18, age_max: 65 }` matches 18 ≤ age < 65. This makes
 * boundary patients (e.g. exactly 18) land deterministically in the
 * adult band and not in the pediatric one.
 */
export function resolveReferenceRange(
	ranges: ReferenceRange[] | null | undefined,
	patient: PatientForRange,
): ReferenceRange | null {
	if (!ranges || ranges.length === 0) return null;
	const ageYears = computeAgeYears(patient.date_of_birth ?? null);
	const sex =
		patient.gender === "male" || patient.gender === "female"
			? patient.gender
			: null;

	let best: { range: ReferenceRange; score: number } | null = null;
	for (const r of ranges) {
		if (r.sex != null && r.sex !== sex) continue;

		const hasAgeBound = r.age_min != null || r.age_max != null;
		if (hasAgeBound) {
			if (ageYears == null) continue;
			if (r.age_min != null && ageYears < r.age_min) continue;
			if (r.age_max != null && ageYears >= r.age_max) continue;
		}

		let score = 0;
		if (r.sex != null) score += 2;
		if (r.age_min != null) score += 1;
		if (r.age_max != null) score += 1;

		if (!best || score > best.score) {
			best = { range: r, score };
		}
	}
	return best?.range ?? null;
}

// ===== Delta-check rules (per-test) =====
//
// A delta check compares the new value against the patient's most recent
// prior value for the same test (within window_days). Triggered swings
// surface a warning modal at result-entry time; 'block' severity is
// belt-and-suspenders enforced server-side via acknowledge_deltas flag.

export const deltaSeveritySchema = z.enum(["warn", "block"]);
export type DeltaSeverity = z.infer<typeof deltaSeveritySchema>;

export const deltaRuleSchema = z
	.object({
		max_pct_change: z.number().positive().max(10000).nullable().optional(),
		max_abs_change: z.number().positive().nullable().optional(),
		window_days: z.number().int().positive().max(3650).default(30),
		severity: deltaSeveritySchema.default("warn"),
		message_ar: z.string().max(500).nullable().optional(),
		message_en: z.string().max(500).nullable().optional(),
	})
	.refine((r) => r.max_pct_change != null || r.max_abs_change != null, {
		message: "At least one of max_pct_change or max_abs_change is required",
	});
export type DeltaRule = z.infer<typeof deltaRuleSchema>;

/**
 * Snapshot of the patient's most recent prior measurement, used for the
 * delta-check modal's "previous value was X on date Y" preview.
 */
export const priorValueSnapshotSchema = z.object({
	value_numeric: z.number(),
	unit: z.string().nullable(),
	measured_at: z.string().datetime(),
});
export type PriorValueSnapshot = z.infer<typeof priorValueSnapshotSchema>;

/** Result of evaluating a delta rule against a (current, prior) pair. */
export interface DeltaCheckOutcome {
	/** True when the rule would trip on these values. */
	trigger: boolean;
	/** Echoed for convenience — null when no rule configured. */
	severity: DeltaSeverity | null;
	/** Absolute change `|new - prior|`. Undefined when no prior. */
	abs_change?: number;
	/** Percentage change `|Δ| / |prior| × 100`. Undefined when no prior or prior=0. */
	pct_change?: number;
	/** Reason the trigger fired (UI annotation). */
	reason?: "pct" | "abs" | "both";
	/** Custom message override from the rule. */
	message_ar?: string | null;
	message_en?: string | null;
}

/**
 * Pure evaluator — given a new value, the patient's prior value (or null
 * when none exists), and the configured rule, returns whether the delta
 * check trips and the metadata needed to render a warning. No DB access.
 *
 *   - No rule configured → never trigger
 *   - No prior value (first-ever measurement) → never trigger
 *   - prior = 0 → only abs_change rule can trigger (pct is undefined / ÷0)
 */
export function evaluateDeltaCheck(
	newValue: number | null | undefined,
	prior: { value_numeric: number; measured_at: string } | null | undefined,
	rule: DeltaRule | null | undefined,
): DeltaCheckOutcome {
	if (newValue == null || rule == null || prior == null) {
		return { trigger: false, severity: rule?.severity ?? null };
	}
	const abs = Math.abs(newValue - prior.value_numeric);
	const pct =
		prior.value_numeric !== 0
			? (abs / Math.abs(prior.value_numeric)) * 100
			: undefined;

	const pctTrip =
		rule.max_pct_change != null && pct != null && pct > rule.max_pct_change;
	const absTrip = rule.max_abs_change != null && abs > rule.max_abs_change;

	if (!pctTrip && !absTrip) {
		return {
			trigger: false,
			severity: rule.severity,
			abs_change: abs,
			pct_change: pct,
		};
	}

	return {
		trigger: true,
		severity: rule.severity,
		abs_change: abs,
		pct_change: pct,
		reason: pctTrip && absTrip ? "both" : pctTrip ? "pct" : "abs",
		message_ar: rule.message_ar ?? null,
		message_en: rule.message_en ?? null,
	};
}

/** Server payload for /clinical/lab-values/delta-check (UI pre-save preview). */
export const deltaCheckInputSchema = z.object({
	patient_id: z.string().uuid(),
	test_name: z.string().min(1).max(200),
	value_numeric: z.number(),
});
export type DeltaCheckInput = z.infer<typeof deltaCheckInputSchema>;

export const deltaCheckResponseSchema = z.object({
	prior: priorValueSnapshotSchema.nullable(),
	outcome: z.object({
		trigger: z.boolean(),
		severity: deltaSeveritySchema.nullable(),
		abs_change: z.number().optional(),
		pct_change: z.number().optional(),
		reason: z.enum(["pct", "abs", "both"]).optional(),
		message_ar: z.string().nullable().optional(),
		message_en: z.string().nullable().optional(),
	}),
});
export type DeltaCheckResponse = z.infer<typeof deltaCheckResponseSchema>;

// ===== Clinic lab tests library (autocomplete source) =====

export const clinicLabTestSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	default_instructions: z.string().nullable(),
	price: z.string().nullable(),
	section: z.string().nullable(),
	default_unit: z.string().nullable(),
	default_ref_low: z.string().nullable(),
	default_ref_high: z.string().nullable(),
	/**
	 * Quality bundle (migration 0065): age/sex-aware reference ranges.
	 * Empty array = clinic hasn't configured multi-range entries yet, fall
	 * back to the flat default_ref_low/high above.
	 */
	reference_ranges: z.array(referenceRangeSchema).default([]),
	/**
	 * Quality bundle (migration 0067): per-test delta-check rule.
	 * NULL = no delta check; UI shows the configuration form unfilled.
	 */
	delta_rule: deltaRuleSchema.nullable().default(null),
	/**
	 * Sprint 2 (migration 0070): per-test TAT target in hours.
	 * NULL = no target configured; the TAT report aggregates stats but
	 * cannot compute an outlier count for the row.
	 */
	target_tat_hours: z.string().nullable().default(null),
	usage_count: z.number().int(),
	last_used_at: z.string().datetime().nullable(),
	is_pinned: z.boolean(),
	is_hidden: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ClinicLabTestApi = z.infer<typeof clinicLabTestSchema>;

// Optional catalogue metadata shared by create + bulk-import.
const labTestMetaSchema = {
	section: z.string().max(60).nullable().optional(),
	default_unit: z.string().max(50).nullable().optional(),
	default_ref_low: z.string().max(40).nullable().optional(),
	default_ref_high: z.string().max(40).nullable().optional(),
};

export const createLabTestInputSchema = z.object({
	name: z.string().min(1).max(200),
	default_instructions: z.string().max(500).optional(),
	price: moneyStringSchema.nullable().optional(),
	is_pinned: z.boolean().optional(),
	...labTestMetaSchema,
});
export type CreateLabTestInput = z.infer<typeof createLabTestInputSchema>;

export const updateLabTestInputSchema = z.object({
	name: z.string().min(1).max(200).optional(),
	default_instructions: z.string().max(500).nullable().optional(),
	price: moneyStringSchema.nullable().optional(),
	is_pinned: z.boolean().optional(),
	is_hidden: z.boolean().optional(),
	/** Replace the entire reference_ranges array atomically. */
	reference_ranges: z.array(referenceRangeSchema).max(20).optional(),
	/** Configure (or clear via null) the delta-check rule for this test. */
	delta_rule: deltaRuleSchema.nullable().optional(),
	/**
	 * Per-test TAT target in hours (numeric string, ≥ 0). Set to null to clear.
	 */
	target_tat_hours: z
		.string()
		.regex(
			/^\d{1,6}(\.\d{1,2})?$/u,
			"Use a numeric value with up to 2 decimals",
		)
		.nullable()
		.optional(),
	...labTestMetaSchema,
});
export type UpdateLabTestInput = z.infer<typeof updateLabTestInputSchema>;

// ===== Lab panels / packages ("فحص شامل") =====

export const clinicLabPanelSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	price: z.string().nullable(),
	tests: z.array(z.string()),
	is_active: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ClinicLabPanelApi = z.infer<typeof clinicLabPanelSchema>;

export const createLabPanelInputSchema = z.object({
	name: z.string().min(1).max(120),
	price: moneyStringSchema.nullable().optional(),
	tests: z.array(z.string().min(1).max(200)).min(1).max(60),
});
export type CreateLabPanelInput = z.infer<typeof createLabPanelInputSchema>;

export const updateLabPanelInputSchema = z.object({
	name: z.string().min(1).max(120).optional(),
	price: moneyStringSchema.nullable().optional(),
	tests: z.array(z.string().min(1).max(200)).min(1).max(60).optional(),
	is_active: z.boolean().optional(),
});
export type UpdateLabPanelInput = z.infer<typeof updateLabPanelInputSchema>;

// ===== Outstanding referrals (clinic-side tracking of routed orders) =====
// The referring clinic's one-stop view of what it sent out and where it stands.
// Built from the clinic's OWN rows (no cross-tenant) — derived status per kind.

export const outstandingReferralStatusSchema = z.enum([
	"routed", // sent, partner hasn't accepted yet
	"accepted", // partner accepted / working
	"ready", // result/report came back
	"rejected", // partner declined
	"dispensed", // pharmacy dispensed
]);
export type OutstandingReferralStatus = z.infer<
	typeof outstandingReferralStatusSchema
>;

export const outstandingReferralRowSchema = z.object({
	kind: z.enum(["lab", "radiology", "pharmacy"]),
	id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string().nullable(),
	partner_name: z.string().nullable(),
	label: z.string(),
	pickup_code: z.string().nullable(),
	status: outstandingReferralStatusSchema,
	at: z.string().datetime(),
});
export type OutstandingReferralRow = z.infer<
	typeof outstandingReferralRowSchema
>;

// ===== Referral (referring) doctors + dues =====

/** Commission percentage as a numeric string ("0".."100", up to 2 decimals). */
export const pctStringSchema = z
	.string()
	.regex(
		/^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/u,
		"Must be 0–100 with up to 2 decimals",
	);
const pctSchema = pctStringSchema;

export const referralDoctorSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	phone: z.string().nullable(),
	commission_pct: z.string().nullable(),
	notes: z.string().nullable(),
	is_active: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ReferralDoctorApi = z.infer<typeof referralDoctorSchema>;

export const createReferralDoctorInputSchema = z.object({
	name: z.string().min(2).max(120),
	phone: z.string().max(40).nullable().optional(),
	commission_pct: pctSchema.nullable().optional(),
	notes: z.string().max(500).nullable().optional(),
});
export type CreateReferralDoctorInput = z.infer<
	typeof createReferralDoctorInputSchema
>;

export const updateReferralDoctorInputSchema = z.object({
	name: z.string().min(2).max(120).optional(),
	phone: z.string().max(40).nullable().optional(),
	commission_pct: pctSchema.nullable().optional(),
	notes: z.string().max(500).nullable().optional(),
	is_active: z.boolean().optional(),
});
export type UpdateReferralDoctorInput = z.infer<
	typeof updateReferralDoctorInputSchema
>;

export const referralDuesQuerySchema = z.object({
	from: z.string().datetime(),
	to: z.string().datetime(),
});
export type ReferralDuesQuery = z.infer<typeof referralDuesQuerySchema>;

/** Per-doctor dues over a window (cash-basis, by order date). */
export const referralDuesRowSchema = z.object({
	doctor_id: z.string().uuid(),
	doctor_name: z.string(),
	order_count: z.number().int(),
	total_revenue: z.string(),
	dues: z.string(),
});
export type ReferralDuesRow = z.infer<typeof referralDuesRowSchema>;

/** One referred order in a doctor's dues breakdown. */
export const referralDuesDetailRowSchema = z.object({
	order_id: z.string().uuid(),
	patient_name: z.string(),
	ordered_at: z.string().datetime(),
	total_price: z.string().nullable(),
	referral_pct: z.string().nullable(),
	dues: z.string(),
});
export type ReferralDuesDetailRow = z.infer<typeof referralDuesDetailRowSchema>;

// ===== Referral-partner commission (cross-facility network) =====
// The PARTNER facility (lab/radiology/pharmacy) optionally pays the referring
// CLINIC a % per accepted referral — the network's only cross-facility money
// flow. A per-link default is set once by the partner (the payer); an optional
// per-referral override applies at accept/sale time; the resolved % is frozen
// onto the accepted order/sale so dues never shift when the default changes.

/** A referring clinic linked to this facility, with its commission default. */
export const referralPartnerSchema = z.object({
	link_id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	clinic_name: z.string(),
	commission_pct: z.string().nullable(),
	created_at: z.string().datetime(),
});
export type ReferralPartnerApi = z.infer<typeof referralPartnerSchema>;

export const updateReferralPartnerInputSchema = z.object({
	commission_pct: pctSchema.nullable(),
});
export type UpdateReferralPartnerInput = z.infer<
	typeof updateReferralPartnerInputSchema
>;

/**
 * Optional body on the lab/radiology accept endpoints — a per-referral
 * commission override. Omitted field = use the link's default; explicit null =
 * no commission for this referral.
 */
export const acceptReferralInputSchema = z.object({
	commission_pct: pctSchema.nullable().optional(),
});
export type AcceptReferralInput = z.infer<typeof acceptReferralInputSchema>;

/** Per-referring-clinic dues over a window (facility = the payer). */
export const referralPartnerDuesRowSchema = z.object({
	clinic_id: z.string().uuid(),
	clinic_name: z.string(),
	order_count: z.number().int(),
	total_revenue: z.string(),
	dues: z.string(),
});
export type ReferralPartnerDuesRow = z.infer<
	typeof referralPartnerDuesRowSchema
>;

/** One accepted referral (order/study/sale) in a clinic's dues breakdown. */
export const referralPartnerDuesDetailRowSchema = z.object({
	kind: z.enum(["lab", "radiology", "pharmacy"]),
	id: z.string().uuid(),
	patient_name: z.string().nullable(),
	at: z.string().datetime(),
	total: z.string().nullable(),
	commission_pct: z.string().nullable(),
	dues: z.string(),
});
export type ReferralPartnerDuesDetailRow = z.infer<
	typeof referralPartnerDuesDetailRowSchema
>;

/** Bulk import from the built-in library (idempotent on name). */
export const bulkImportLabTestsInputSchema = z.object({
	tests: z
		.array(
			z.object({
				name: z.string().min(1).max(200),
				section: z.string().max(60).optional(),
				default_unit: z.string().max(50).optional(),
				default_ref_low: z.string().max(40).optional(),
				default_ref_high: z.string().max(40).optional(),
			}),
		)
		.min(1)
		.max(300),
});
export type BulkImportLabTestsInput = z.infer<
	typeof bulkImportLabTestsInputSchema
>;

export const labTestSearchQuerySchema = z.object({
	q: z.string().max(100).optional(),
	limit: z.coerce.number().int().min(1).max(20).default(8),
});
export type LabTestSearchQuery = z.infer<typeof labTestSearchQuerySchema>;

// ===== Lab results (file uploads attached to an order) =====

export const ALLOWED_LAB_RESULT_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/heic",
	"application/pdf",
] as const;
export const MAX_LAB_RESULT_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

export const labResultStatusSchema = z.enum(["pending", "ready", "failed"]);
export type LabResultStatus = z.infer<typeof labResultStatusSchema>;

export const labResultSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	lab_order_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	uploaded_by_member_id: z.string().uuid().nullable(),
	r2_key: z.string(),
	mime_type: z.string(),
	size_bytes: z.number().int().nullable(),
	caption: z.string().nullable(),
	status: labResultStatusSchema,
	taken_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	/**
	 * Short-lived (5 min) presigned GET URL the UI can drop straight into
	 * <img src> or an <a href> for download. Generated server-side on each
	 * list/get; null when status !== 'ready' or R2 not configured.
	 */
	view_url: z.string().nullable().optional(),
});
export type LabResultApi = z.infer<typeof labResultSchema>;

export const requestLabResultUploadInputSchema = z.object({
	caption: z.string().max(500).optional(),
	mime_type: z.enum(ALLOWED_LAB_RESULT_MIME_TYPES),
	size_bytes: z.number().int().positive().max(MAX_LAB_RESULT_SIZE_BYTES),
	taken_at: z.string().datetime().optional(),
});
export type RequestLabResultUploadInput = z.infer<
	typeof requestLabResultUploadInputSchema
>;

export const updateLabResultInputSchema = z.object({
	caption: z.string().max(500).nullable().optional(),
	taken_at: z.string().datetime().nullable().optional(),
});
export type UpdateLabResultInput = z.infer<typeof updateLabResultInputSchema>;

// ===== Patient documents (referrals, imaging, insurance, etc.) =====

export const documentCategorySchema = z.enum([
	"external_lab",
	"referral",
	"imaging",
	"insurance",
	"consent",
	"other",
]);
export type DocumentCategory = z.infer<typeof documentCategorySchema>;

export const ALLOWED_DOCUMENT_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/heic",
	"application/pdf",
] as const;
export const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

export const documentStatusSchema = z.enum(["pending", "ready", "failed"]);
export type DocumentStatus = z.infer<typeof documentStatusSchema>;

export const patientDocumentSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	uploaded_by_member_id: z.string().uuid().nullable(),
	category: documentCategorySchema,
	title: z.string(),
	description: z.string().nullable(),
	r2_key: z.string(),
	mime_type: z.string(),
	size_bytes: z.number().int().nullable(),
	status: documentStatusSchema,
	document_date: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	/**
	 * Short-lived (5 min) presigned GET URL. Generated server-side on each
	 * list/get; null when status !== 'ready' or R2 not configured.
	 */
	view_url: z.string().nullable().optional(),
});
export type PatientDocumentApi = z.infer<typeof patientDocumentSchema>;

export const requestDocumentUploadInputSchema = z.object({
	category: documentCategorySchema,
	title: z.string().min(1).max(200),
	description: z.string().max(2000).nullish(),
	mime_type: z.enum(ALLOWED_DOCUMENT_MIME_TYPES),
	size_bytes: z.number().int().positive().max(MAX_DOCUMENT_SIZE_BYTES),
	document_date: z.string().datetime().optional(),
});
export type RequestDocumentUploadInput = z.infer<
	typeof requestDocumentUploadInputSchema
>;

export const updateDocumentInputSchema = z.object({
	category: documentCategorySchema.optional(),
	title: z.string().min(1).max(200).optional(),
	description: z.string().max(2000).nullable().optional(),
	document_date: z.string().datetime().nullable().optional(),
});
export type UpdateDocumentInput = z.infer<typeof updateDocumentInputSchema>;

export const listDocumentsQuerySchema = z.object({
	category: documentCategorySchema.optional(),
});
export type ListDocumentsQuery = z.infer<typeof listDocumentsQuerySchema>;

// ===== Drug interaction checks (AI-powered) =====

export const interactionSeveritySchema = z.enum([
	"critical",
	"moderate",
	"info",
]);
export type InteractionSeverity = z.infer<typeof interactionSeveritySchema>;

export const interactionCategorySchema = z.enum([
	"drug_drug", // Two drugs interact with each other
	"drug_allergy", // Drug conflicts with a recorded allergy
	"drug_condition", // Drug contraindicated by chronic condition
	"drug_pregnancy", // Drug is teratogenic / contraindicated in active pregnancy
	"duplicate_therapy", // Two drugs cover the same indication
	"dosage_concern", // Dose exceeds the established daily maximum (deterministic)
	"controlled_substance", // Controlled/scheduled drug — compliant prescribing + record
]);
export type InteractionCategory = z.infer<typeof interactionCategorySchema>;

export const interactionWarningSchema = z.object({
	severity: interactionSeveritySchema,
	category: interactionCategorySchema,
	/** Drugs involved (e.g. ['Augmentin 625mg', 'Aspirin']) */
	drugs: z.array(z.string()),
	/** Short Arabic explanation the doctor can scan in 1-2 seconds. */
	summary_ar: z.string(),
	/** Optional longer English/clinical detail. */
	detail_en: z.string().optional(),
	/** What the doctor should consider doing (e.g. "خفض الجرعة"). */
	recommendation_ar: z.string().optional(),
	/**
	 * Where the warning came from:
	 *  - 'rule' = a deterministic, curated critical-interaction rule (reliable,
	 *    fires the same way every time for the known-dangerous set)
	 *  - 'ai'   = the AI long-tail check (assistive, may vary)
	 * Defaults to 'ai' for back-compat with older AI-only payloads.
	 */
	source: z.enum(["rule", "ai"]).default("ai"),
});
export type InteractionWarning = z.infer<typeof interactionWarningSchema>;

export const checkInteractionInputSchema = z.object({
	patient_id: z.string().uuid(),
	/**
	 * The prescription items the doctor is about to issue — the FULL list as
	 * typed. A row whose dosage/frequency is still empty must STILL be checked
	 * (the drug name is what drives interactions), so the mandatory-field rule
	 * is relaxed here; only the name is required.
	 */
	new_items: z
		.array(
			prescriptionItemDraftSchema.extend({
				medication_name: z.string().min(1).max(200),
			}),
		)
		.min(1)
		.max(20),
});
export type CheckInteractionInput = z.infer<typeof checkInteractionInputSchema>;

export const checkInteractionResponseSchema = z.object({
	warnings: z.array(interactionWarningSchema),
	/** Cache hit (we returned a recent identical check). */
	cached: z.boolean(),
	/** True when the AI pass ran; false when it was skipped (e.g. over the
	 *  monthly AI budget) and only deterministic rule warnings were returned —
	 *  the UI shows the AI badge only when true. */
	ai_generated: z.boolean(),
	/** Disclaimer text the UI should display. */
	disclaimer_ar: z.string(),
});
export type CheckInteractionResponse = z.infer<
	typeof checkInteractionResponseSchema
>;

// ===== Structured lab values (extracted from lab result files) =====

/** Provenance of a structured lab value — where it came from. */
export const LAB_VALUE_SOURCES = [
	"linked_lab",
	"instrument",
	"manual",
	"ai_extracted",
] as const;
export const labValueSourceSchema = z.enum(LAB_VALUE_SOURCES);
export type LabValueSource = z.infer<typeof labValueSourceSchema>;

export const LAB_VALUE_SOURCE_LABELS: Record<
	LabValueSource,
	{ ar: string; en: string }
> = {
	linked_lab: { ar: "مختبر مرتبط", en: "Linked lab" },
	instrument: { ar: "جهاز مختبر", en: "Instrument" },
	manual: { ar: "إدخال يدوي", en: "Manual entry" },
	ai_extracted: { ar: "استخراج ذكي", en: "AI-extracted" },
};

export const labResultValueSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	lab_result_id: z.string().uuid().nullable(),
	lab_order_id: z.string().uuid().nullable(),
	patient_id: z.string().uuid(),
	test_name: z.string(),
	value_numeric: z.number().nullable(),
	value_text: z.string().nullable(),
	unit: z.string().nullable(),
	reference_low: z.number().nullable(),
	reference_high: z.number().nullable(),
	reference_text: z.string().nullable(),
	abnormal_flag: abnormalFlagSchema,
	notes: z.string().nullable(),
	verified_at: z.string().datetime().nullable(),
	verified_by_member_id: z.string().uuid().nullable(),
	auto_released: z.boolean(),
	source: labValueSourceSchema.nullable(),
	/** WHO typed the value (dual-verification anchor). Null = legacy/system. */
	entered_by_member_id: z.string().uuid().nullable().optional(),
	/** Set when this verified value was corrected post-release (§5.8.3). */
	amended_at: z.string().datetime().nullable().optional(),
	measured_at: z.string().datetime(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type LabResultValueApi = z.infer<typeof labResultValueSchema>;

// ===== Amended results (ISO 15189 §5.8.3) =====

/**
 * Amend a VERIFIED lab value. The previous value/unit/flag are snapshotted
 * into lab_value_amendments inside the same transaction; abnormal_flag is
 * recomputed server-side. A reason is mandatory (medico-legal trail).
 */
export const amendLabValueInputSchema = z
	.object({
		value_numeric: z.number().optional(),
		value_text: z.string().max(200).optional(),
		unit: z.string().max(50).optional(),
		reason: z.string().min(1).max(500),
	})
	.refine((v) => v.value_numeric !== undefined || v.value_text !== undefined, {
		message: "value_numeric or value_text is required",
	});
export type AmendLabValueInput = z.infer<typeof amendLabValueInputSchema>;

/** One history row behind a value's «معدّل» marker. */
export const labValueAmendmentSchema = z.object({
	id: z.string().uuid(),
	value_id: z.string().uuid(),
	previous_value_numeric: z.number().nullable(),
	previous_value_text: z.string().nullable(),
	previous_unit: z.string().nullable(),
	previous_flag: z.string().nullable(),
	reason: z.string(),
	amended_by_member_id: z.string().uuid().nullable(),
	amended_by_name: z.string().nullable(),
	created_at: z.string().datetime(),
});
export type LabValueAmendmentApi = z.infer<typeof labValueAmendmentSchema>;

// ===== Panel components (CBC etc. — one ordered test, many analytes) =====

/**
 * One component of a panel test as served to the entry grid. `id` is the
 * clinic's lab_test_components row when the clinic customized the panel;
 * null when the definition comes from the built-in library fallback
 * (`source` disambiguates).
 */
export const labComponentSchema = z.object({
	id: z.string().uuid().nullable(),
	name: z.string(),
	name_ar: z.string().nullable(),
	unit: z.string().nullable(),
	ref_low: z.number().nullable(),
	ref_high: z.number().nullable(),
	code: z.string().nullable(),
	sort_order: z.number().int(),
	source: z.enum(["clinic", "library"]),
});
export type LabComponentApi = z.infer<typeof labComponentSchema>;

/** Query for GET /clinical/lab-components — the ordered test's name. */
export const labComponentsQuerySchema = z.object({
	test_name: z.string().min(1).max(200),
});
export type LabComponentsQuery = z.infer<typeof labComponentsQuerySchema>;

/** Manually verify (sign off) a set of lab values — typically abnormal ones. */
export const verifyLabValuesInputSchema = z.object({
	value_ids: z.array(z.string().uuid()).min(1).max(200),
});
export type VerifyLabValuesInput = z.infer<typeof verifyLabValuesInputSchema>;

export const createLabResultValueInputSchema = z.object({
	test_name: z.string().min(1).max(200),
	value_numeric: z.number().optional(),
	value_text: z.string().max(200).optional(),
	unit: z.string().max(50).optional(),
	reference_low: z.number().optional(),
	reference_high: z.number().optional(),
	reference_text: z.string().max(100).optional(),
	notes: z.string().max(500).optional(),
	measured_at: z.string().datetime().optional(),
});
export type CreateLabResultValueInput = z.infer<
	typeof createLabResultValueInputSchema
>;

/**
 * Batch create — typical usage is "I read the patient's CBC report,
 * here are 8 values". Single-shot insert via array.
 *
 * `acknowledge_deltas` is the lab tech's explicit override for any
 * delta-check rules whose severity is 'block'. When false (default), the
 * server rejects the insert if any block-severity rule trips; the UI is
 * expected to surface the warning modal and only retry with the flag.
 */
export const createLabResultValuesBatchSchema = z.object({
	lab_result_id: z.string().uuid().optional(),
	lab_order_id: z.string().uuid().optional(),
	patient_id: z.string().uuid(),
	values: z.array(createLabResultValueInputSchema).min(1).max(50),
	acknowledge_deltas: z.boolean().optional(),
	/** Provenance for the whole batch. Defaults to 'manual' when omitted. */
	source: labValueSourceSchema.optional(),
	/**
	 * Idempotency key for this save action. When the same key reaches the
	 * server twice (double-tap, retried request, instrument re-send) only the
	 * first insert happens; replays return the originally-inserted rows. The
	 * caller MUST keep the key stable across retries of the same logical save
	 * and rotate it for a genuinely new save.
	 */
	idempotency_key: z.string().min(8).max(200).optional(),
});
export type CreateLabResultValuesBatchInput = z.infer<
	typeof createLabResultValuesBatchSchema
>;

// ===== AI lab-value extraction (photo/PDF of an outside report → draft values) =====

/**
 * Image types Claude vision can read. NOTE: HEIC is intentionally absent —
 * the model's image blocks don't accept it (the file uploader allows HEIC for
 * storage, but extraction needs a real raster the model can decode). PDFs go
 * through the document block.
 */
export const AI_EXTRACT_IMAGE_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
] as const;
export const AI_EXTRACT_MIME_TYPES = [
	...AI_EXTRACT_IMAGE_MIME_TYPES,
	"application/pdf",
] as const;

export const extractLabValuesInputSchema = z.object({
	/** base64 (no data: prefix) of the image or PDF. Cap ~10MB binary. */
	image_base64: z.string().min(16).max(14_000_000),
	mime_type: z.enum(AI_EXTRACT_MIME_TYPES),
	/** Optional, for the medico-legal audit row. */
	patient_id: z.string().uuid().optional(),
	lab_order_id: z.string().uuid().optional(),
});
export type ExtractLabValuesInput = z.infer<typeof extractLabValuesInputSchema>;

/** One extracted row — the doctor reviews/edits before anything is saved. */
export const extractedLabValueSchema = z.object({
	test_name: z.string().min(1).max(200),
	value_numeric: z.number().nullable().optional(),
	value_text: z.string().max(200).nullable().optional(),
	unit: z.string().max(50).nullable().optional(),
	reference_low: z.number().nullable().optional(),
	reference_high: z.number().nullable().optional(),
});
export type ExtractedLabValue = z.infer<typeof extractedLabValueSchema>;

export const extractLabValuesResultSchema = z.object({
	values: z.array(extractedLabValueSchema),
});
export type ExtractLabValuesResult = z.infer<
	typeof extractLabValuesResultSchema
>;

// ===== AI lab review — decision SUPPORT for the doctor (trends old vs new) =====

export const labReviewInputSchema = z.object({
	patient_id: z.string().uuid(),
});
export type LabReviewInput = z.infer<typeof labReviewInputSchema>;

export const labReviewTrendSchema = z.object({
	test_name: z.string(),
	direction: z.enum(["rising", "falling", "stable", "fluctuating"]),
	interpretation: z.string(),
});
export type LabReviewTrend = z.infer<typeof labReviewTrendSchema>;

export const labReviewNotableSchema = z.object({
	test_name: z.string(),
	finding: z.string(),
	significance: z.enum(["high", "moderate", "low"]),
});
export type LabReviewNotable = z.infer<typeof labReviewNotableSchema>;

/**
 * Doctor-facing AI reading of a patient's lab history: trend deltas (old vs
 * new), notable abnormals, and considerations. NOT a diagnosis — assistive
 * only; the app shows a disclaimer.
 */
export const labReviewResultSchema = z.object({
	summary: z.string(),
	trends: z.array(labReviewTrendSchema),
	notable: z.array(labReviewNotableSchema),
	considerations: z.array(z.string()),
});
export type LabReviewResult = z.infer<typeof labReviewResultSchema>;

// ===== Encounter Synthesis — the unified visit co-pilot =====
//
// ONE action that reads the WHOLE encounter — chief complaint + the doctor's
// notes + vitals + the patient's allergies/meds/conditions + the CURRENT and
// PRIOR structured lab values (so it compares old vs new) + finalized radiology
// impressions — and returns ONE consolidated DRAFT: a clinical picture, a
// lab/radiology comparison, ranked differentials, ICD-10-coded diagnoses
// (grounded server-side against the real catalogue), an allergy-aware
// prescription draft, and a follow-up suggestion. Assistive only: the physician
// reviews + one-tap applies each part. Solves the "results came back, now the
// doctor has to stitch 5 screens together" problem.

export const encounterSynthesisInputSchema = z.object({
	patient_id: z.string().uuid(),
	/** The visit being worked up (its notes/diagnosis/plan feed the context). */
	visit_id: z.string().uuid().optional(),
	appointment_id: z.string().uuid().optional(),
	/** The doctor's current notes, sent from the visit form (like the copilot).
	 *  The server adds the structured labs, radiology, and patient history. */
	specialty: z.string().max(60).optional(),
	chief_complaint: z.string().max(1000).optional(),
	subjective: z.string().max(4000).optional(),
	objective: z.string().max(4000).optional(),
	assessment: z.string().max(4000).optional(),
	plan: z.string().max(4000).optional(),
	vitals: copilotVitalsSchema.optional(),
});
export type EncounterSynthesisInput = z.infer<
	typeof encounterSynthesisInputSchema
>;

/** Draft medication — lenient so a partial AI suggestion never fails the whole
 *  synthesis; the doctor completes any blanks in the prescription form. */
export const encounterMedicationSchema = z.object({
	medication_name: z.string(),
	dosage: z.string().default(""),
	frequency: z.string().default(""),
	duration_days: z.number().int().positive().max(365).nullable().default(null),
	instructions: z.string().default(""),
});
export type EncounterMedication = z.infer<typeof encounterMedicationSchema>;

export const encounterRadiologyHighlightSchema = z.object({
	study: z.string(),
	highlight: z.string(),
});

export const encounterFollowUpSchema = z.object({
	recommended: z.boolean().default(false),
	in_days: z.number().int().positive().max(365).nullable().default(null),
	reason: z.string().default(""),
});

export const encounterSynthesisResultSchema = z.object({
	/** The clinical picture in ≤3 sentences, integrating notes + results. */
	summary: z.string().default(""),
	/** Old→new lab trend deltas (the follow-up comparison). */
	lab_trends: z.array(labReviewTrendSchema).default([]),
	/** Current out-of-range values that matter. */
	lab_notable: z.array(labReviewNotableSchema).default([]),
	/** Key radiology impressions the AI factored in. */
	radiology_highlights: z.array(encounterRadiologyHighlightSchema).default([]),
	differentials: z.array(copilotDifferentialSchema).default([]),
	/** ICD-10 diagnoses — grounded server-side against the real catalogue. */
	diagnoses: z.array(diagnosisCodeSchema).default([]),
	/** Allergy-aware prescription draft (doctor reviews + the existing
	 *  interaction/allergy guards still run on issue). */
	medications: z.array(encounterMedicationSchema).default([]),
	follow_up: encounterFollowUpSchema.nullable().default(null),
	red_flags: z.array(copilotRedFlagSchema).default([]),
	/** Transparency: what the AI actually had in front of it. */
	data_seen: z
		.object({
			lab_count: z.number().int().default(0),
			radiology_count: z.number().int().default(0),
			has_vitals: z.boolean().default(false),
			has_notes: z.boolean().default(false),
			/** Active (ongoing) problems from the structured problem list. */
			active_problem_count: z.number().int().default(0),
			/** Resolved/past problems read as historical context only. */
			resolved_problem_count: z.number().int().default(0),
		})
		.default({
			lab_count: 0,
			radiology_count: 0,
			has_vitals: false,
			has_notes: false,
			active_problem_count: 0,
			resolved_problem_count: 0,
		}),
});
export type EncounterSynthesisResult = z.infer<
	typeof encounterSynthesisResultSchema
>;

/** Counts of this patient's ordered tests that have not returned yet — drives
 *  the pre-synthesis "results still pending" warning. */
export interface PendingResultsCount {
	/** Lab orders still in `pending` (specimen/results not in). */
	pending_labs: number;
	/** Radiology orders still `pending` or `in_progress` (not finalized). */
	pending_radiology: number;
}

// Synthesis runs as an async job (the LLM call takes 40-90s — far past any
// proxy timeout). POST returns { job_id } immediately; the client polls.
export const synthesisJobStatuses = [
	"queued",
	"running",
	"succeeded",
	"failed",
] as const;
export type SynthesisJobStatus = (typeof synthesisJobStatuses)[number];

export interface SynthesisJobStartResponse {
	job_id: string;
}

/** Machine-readable failure code stored on the job; the client translates. */
export type SynthesisJobErrorCode =
	| "timeout"
	| "ai_failed"
	| "feature_disabled"
	| "not_found"
	| "internal";

export interface SynthesisJobApi {
	id: string;
	status: SynthesisJobStatus;
	/** Present only when status = succeeded. */
	result: EncounterSynthesisResult | null;
	/** SynthesisJobErrorCode when status = failed. */
	error: string | null;
	created_at: string;
	started_at: string | null;
	finished_at: string | null;
}

/**
 * Compute the abnormal flag from a numeric value + reference range.
 * Used both server-side (on insert) and client-side (preview).
 *
 * Two-tier critical detection:
 *   1. Explicit thresholds (criticalLow / criticalHigh) — set by the lab
 *      from medical literature for that test. When provided, these win.
 *   2. Fallback heuristic — ≥30% past the relevant normal boundary. Used
 *      when the lab hasn't configured explicit critical bands yet.
 *
 * The signature is back-compat: existing 3-arg callers keep working with
 * the heuristic. Lab quality bundle (migration 0065) supplies the extra
 * thresholds via configured reference_ranges.
 */
export function computeAbnormalFlag(
	valueNumeric: number | null | undefined,
	refLow: number | null | undefined,
	refHigh: number | null | undefined,
	criticalLow?: number | null,
	criticalHigh?: number | null,
): AbnormalFlag {
	if (valueNumeric == null) return "normal";

	// Explicit critical thresholds take precedence over the heuristic.
	if (criticalHigh != null && valueNumeric > criticalHigh)
		return "critical_high";
	if (criticalLow != null && valueNumeric < criticalLow) return "critical_low";

	const hasLow = refLow != null;
	const hasHigh = refHigh != null;
	if (!hasLow && !hasHigh) {
		// No reference range at all. If the test ALSO has no critical thresholds
		// (checked above), a numeric result is genuinely UN-ASSESSABLE — calling it
		// "normal" would hide a possibly dangerous value on a test the lab never
		// configured (F1.2). Flag it for human review instead. When critical
		// thresholds exist and the value cleared them above, we DO have a safety
		// read, so it stays "normal".
		const hasCriticalBand = criticalLow != null || criticalHigh != null;
		return hasCriticalBand ? "normal" : "needs_review";
	}
	if (hasHigh && valueNumeric > refHigh!) {
		// No explicit critical_high configured → fall back to ×1.3 heuristic. (Kept
		// deliberately: on an UNCONFIGURED analyte the heuristic catches genuine
		// criticals — e.g. WBC 2.5, severe — that would otherwise read as just
		// "low/high". The audit's fabrication concern (a mild elevation flagged
		// critical) is the lesser harm vs missing a real critical; dangerous
		// analytes also carry seeded explicit thresholds which win above. See P2-S3.)
		if (criticalHigh == null && valueNumeric > refHigh! * 1.3)
			return "critical_high";
		return "high";
	}
	if (hasLow && valueNumeric < refLow!) {
		if (criticalLow == null && valueNumeric < refLow! * 0.7)
			return "critical_low";
		return "low";
	}
	return "normal";
}

// ===== Critical value alerts (ISO 15189 §5.8.7) =====
//
// Whenever a lab result value lands in critical_high/critical_low, the
// server creates a paired alert here. UI surfaces pending alerts on a
// sticky red banner and the lab staff must record who they called, how,
// when, and (optionally) that the recipient confirmed receipt.

/** Communication channel used to notify the receiving clinician. */
export const notificationMethodSchema = z.enum([
	"phone",
	"whatsapp",
	"in_person",
	"sms",
	"other",
]);
export type NotificationMethod = z.infer<typeof notificationMethodSchema>;

/** Lifecycle of a critical-value alert. */
export const criticalAlertStatusSchema = z.enum([
	"pending",
	"notified",
	"acknowledged",
]);
export type CriticalAlertStatus = z.infer<typeof criticalAlertStatusSchema>;

/** The flag values that trigger an alert. */
export const criticalAlertFlagSchema = z.enum([
	"critical_high",
	"critical_low",
]);
export type CriticalAlertFlag = z.infer<typeof criticalAlertFlagSchema>;

export const criticalAlertSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	lab_result_value_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	lab_order_id: z.string().uuid().nullable(),

	// Snapshot — frozen at insert time
	test_name: z.string(),
	value_numeric: z.number().nullable(),
	value_text: z.string().nullable(),
	unit: z.string().nullable(),
	reference_low: z.number().nullable(),
	reference_high: z.number().nullable(),
	flag: criticalAlertFlagSchema,

	status: criticalAlertStatusSchema,

	notified_at: z.string().datetime().nullable(),
	notified_by_member_id: z.string().uuid().nullable(),
	notification_method: notificationMethodSchema.nullable(),
	notification_recipient: z.string().nullable(),
	notification_notes: z.string().nullable(),

	acknowledged_at: z.string().datetime().nullable(),
	acknowledged_by_member_id: z.string().uuid().nullable(),

	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),

	// Helpful joins surfaced for UI without forcing a second round-trip.
	patient_name: z.string().optional(),
	patient_phone: z.string().nullable().optional(),
	notified_by_name: z.string().nullable().optional(),
	acknowledged_by_name: z.string().nullable().optional(),
});
export type CriticalAlertApi = z.infer<typeof criticalAlertSchema>;

/** Filter query for the alerts list page (status + window). */
export const listCriticalAlertsQuerySchema = z.object({
	status: criticalAlertStatusSchema.optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListCriticalAlertsQuery = z.infer<
	typeof listCriticalAlertsQuerySchema
>;

/** Payload when staff marks an alert as notified (logs the call). */
export const markCriticalAlertNotifiedInputSchema = z.object({
	method: notificationMethodSchema,
	// Closed-loop (ISO 15189 §5.8.7): WHO was told must be a real identifier
	// (clinician name / phone), not a placeholder like "." — at least 2
	// letter/digit characters (audit: free-text recipient was unvalidated).
	recipient: z
		.string()
		.min(1)
		.max(200)
		.refine((v) => (v.match(/[\p{L}\p{N}]/gu) ?? []).length >= 2, {
			message: "سجّل اسم أو رقم المتلقّي الفعلي (حرفان على الأقل).",
		}),
	notes: z.string().max(2000).optional().nullable(),
});
export type MarkCriticalAlertNotifiedInput = z.infer<
	typeof markCriticalAlertNotifiedInputSchema
>;

/**
 * Batch variant — mark MANY pending alerts notified in one action with the
 * SAME contact metadata. Used by the lab "notify all critical results for
 * this patient" button. The per-result audit trail is preserved: the same
 * who/when/how is written onto every alert row individually.
 */
export const batchMarkCriticalAlertsNotifiedInputSchema =
	markCriticalAlertNotifiedInputSchema.extend({
		alert_ids: z.array(z.string().uuid()).min(1).max(100),
	});
export type BatchMarkCriticalAlertsNotifiedInput = z.infer<
	typeof batchMarkCriticalAlertsNotifiedInputSchema
>;

// ===== Cross-facility lab flow (lab-facing; defined after the schemas it extends) =====

/** A routed order in a lab's incoming worklist (adds the ordering clinic name). */
export const incomingLabOrderSchema = labWorklistItemSchema.extend({
	clinic_id: z.string().uuid(),
	clinic_name: z.string(),
	/** The link's default commission % (prefills the accept-time override). */
	link_commission_pct: z.string().nullable().optional(),
});
export type IncomingLabOrderApi = z.infer<typeof incomingLabOrderSchema>;

/** Lab records results onto a routed order. patient/clinic derived from the order. */
export const recordIncomingResultsSchema = z.object({
	lab_order_id: z.string().uuid(),
	values: z.array(createLabResultValueInputSchema).min(1).max(50),
	idempotency_key: z.string().min(8).max(200).optional(),
});
export type RecordIncomingResultsInput = z.infer<
	typeof recordIncomingResultsSchema
>;

// ===== Onboarding (first-run setup checklist) =====

export const onboardingStepSchema = z.object({
	key: z.string(),
	done: z.boolean(),
});
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

export const onboardingStatusSchema = z.object({
	facility_type: z.enum(["clinic", "lab", "pharmacy", "radiology"]),
	steps: z.array(onboardingStepSchema),
	all_done: z.boolean(),
});
export type OnboardingStatusApi = z.infer<typeof onboardingStatusSchema>;

// ===== Cross-facility pharmacy flow (incoming prescriptions) =====

export const incomingPrescriptionSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	clinic_name: z.string(),
	patient_id: z.string().uuid(),
	patient_name: z.string(),
	items: z.array(prescriptionItemSchema),
	notes_ar: z.string().nullable(),
	issued_at: z.string().datetime(),
	dispensed_at: z.string().datetime().nullable(),
	pickup_code: z.string().nullable(),
});
export type IncomingPrescriptionApi = z.infer<
	typeof incomingPrescriptionSchema
>;

/**
 * Returned when a pharmacy ACCEPTS a referred prescription: the patient is
 * inherited into the pharmacy tenant + the rx marked dispensed, and the UI
 * opens the POS pre-filled with this patient + the prescribed items as a
 * reference checklist.
 */
export const acceptedPrescriptionSchema = z.object({
	prescription_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string(),
	items: z.array(prescriptionItemSchema),
	/** The link's default commission % — POS prefill; the sale snapshots it. */
	commission_pct: z.string().nullable().optional(),
});
export type AcceptedPrescriptionApi = z.infer<
	typeof acceptedPrescriptionSchema
>;

/** POS lookup of an IN-HOUSE prescription (same clinic, not a cross-tenant
 *  referral) so the pharmacist can pull a doctor's rx into the till by patient
 *  name, pickup code, or share link instead of re-typing every medication. */
export const prescriptionLookupInputSchema = z.object({
	query: z.string().trim().min(2).max(120),
});
export type PrescriptionLookupInput = z.infer<
	typeof prescriptionLookupInputSchema
>;

export const prescriptionLookupResultSchema = z.object({
	prescription_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string(),
	items: z.array(prescriptionItemSchema),
	pickup_code: z.string().nullable(),
	share_token: z.string(),
	dispensed_at: z.string().nullable(),
	issued_at: z.string(),
});
export type PrescriptionLookupResult = z.infer<
	typeof prescriptionLookupResultSchema
>;
